import { useQuery } from '@tanstack/react-query';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';

// Datos de /api/proyectos/solicitudes (guía 7.2).

export const PRIORITIES = ['alta', 'media', 'baja'] as const;
export type Priority = (typeof PRIORITIES)[number];
export type RequestStatus = 'jefe' | 'viabilidad' | 'gerente' | 'aprobada' | 'rechazada' | 'retirada';
export type Stage = 'jefe' | 'viabilidad' | 'gerente';
export const STAGES: readonly Stage[] = ['jefe', 'viabilidad', 'gerente'];
export type RequestScope = 'propias' | 'por_aprobar' | 'todas';

export interface RequestListItem {
  id: string;
  number: number;
  code: string;
  title: string;
  organizationId: string;
  organization: string | null;
  requesterId: string;
  requester: string | null;
  status: RequestStatus;
  perceivedUrgency: Priority;
  /** Solo para el jefe directo e IT (no se le muestra al solicitante). */
  priority?: Priority | null;
  stageSince: string;
  createdAt: string;
}

export interface Approval {
  stage: Stage;
  approved: boolean;
  reason: string | null;
  decidedBy: string;
  decidedByName: string | null;
  decidedAt: string;
}

export interface RequestDetail extends Omit<RequestListItem, 'requester'> {
  description: string;
  expectedBenefit: string;
  affectedArea: string | null;
  idealDate: string | null;
  requester: string | null;
  managerId: string | null;
  manager: string | null;
  projectTypeId: string | null;
  projectType: string | null;
  approvals: Approval[];
  project: { id: string; number: number; code: string } | null;
  can: { decide: boolean; setPriority: boolean; chooseType: boolean; withdraw: boolean; attach: boolean; manageAttachments: boolean };
}

export interface RequestFormData {
  organizations: Array<{ id: string; name: string; groupName: string }>;
  defaultOrganizationId: string;
  isVp: boolean;
  manager: string | null;
  /** Solo para IT (viabilidad o aprobación final). */
  projectTypes?: Array<{ id: string; name: string }>;
}

export const OPEN_STATUSES: readonly RequestStatus[] = ['jefe', 'viabilidad', 'gerente'];

export const useRequestForm = () => useQuery({ queryKey: ['solicitudes', 'formulario'], queryFn: () => api<RequestFormData>('/api/proyectos/solicitudes/formulario') });
export const useRequest = (number: string) => useQuery({ queryKey: ['solicitudes', 'detalle', number], queryFn: () => api<RequestDetail>(`/api/proyectos/solicitudes/${number}`) });

export const statusName = (s: RequestStatus) => t(`sp.estado_${s}` as MessageKey);
export const priorityName = (p: Priority) => t(`sp.prioridad_${p}` as MessageKey);
export const stageName = (s: Stage) => t(`sp.etapa_${s}` as MessageKey);

const TONES = { jefe: 'amber', viabilidad: 'amber', gerente: 'amber', aprobada: 'blue', rechazada: 'red', retirada: 'slate' } as const;
export const statusTone = (s: RequestStatus) => TONES[s];

export const formatDate = (iso: string) => new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso).toLocaleDateString('es-PA', { day: 'numeric', month: 'short', year: 'numeric' });
